/**
 * ArrowTile - Force tile that pushes entities in a direction (parameterized by direction)
 * Single class handles all 4 arrow directions
 */

import Phaser from "phaser";
import { BaseTile, TextureDescriptor } from "./BaseTile";
import { TILE } from "../constants/gameConstants";
import { TileIndex } from "../types";
import { GAME_CONFIG } from "../config";

export type ArrowDirection = "up" | "down" | "left" | "right";

export class ArrowTile extends BaseTile {
  private direction: ArrowDirection;

  constructor({
    col,
    row,
    direction,
    id,
  }: {
    col: number;
    row: number;
    direction: ArrowDirection;
    id?: string;
  }) {
    super(id ?? `arrow-${direction}-${col}-${row}`, col, row);
    this.direction = direction;
  }

  tileType(): TileIndex {
    switch (this.direction) {
      case "up":
        return TILE.ARROW_UP;
      case "down":
        return TILE.ARROW_DOWN;
      case "left":
        return TILE.ARROW_LEFT;
      case "right":
        return TILE.ARROW_RIGHT;
    }
  }

  textureDescriptor(): TextureDescriptor {
    return {
      type: "procedural",
      generator: (scene: Phaser.Scene, key: string) => {
        ArrowTile.generateTexture(scene, key, this.direction);
      },
    };
  }

  static generateTexture(
    scene: Phaser.Scene,
    key: string,
    direction: ArrowDirection
  ): void {
    const size = GAME_CONFIG.tileSize;
    const graphics = scene.add.graphics();

    // Black background
    graphics.fillStyle(0x000000, 1);
    graphics.fillRect(0, 0, size, size);

    // Colors for the chevron gradient
    const lightGreen = 0x58d858;
    const darkGreen = 0x008800;

    // Draw chevron pointing up (we'll rotate for other directions)
    // Light green top part of chevron
    graphics.fillStyle(lightGreen, 1);
    graphics.beginPath();
    graphics.moveTo(size / 2, 7);
    graphics.lineTo(size - 7, 24);
    graphics.lineTo(size / 2, 18);
    graphics.lineTo(7, 24);
    graphics.closePath();
    graphics.fillPath();

    // Dark green bottom part of chevron
    graphics.fillStyle(darkGreen, 1);
    graphics.beginPath();
    graphics.moveTo(7, 24);
    graphics.lineTo(size / 2, 18);
    graphics.lineTo(size - 7, 24);
    graphics.lineTo(size - 7, 37);
    graphics.lineTo(size / 2, 31);
    graphics.lineTo(7, 37);
    graphics.closePath();
    graphics.fillPath();

    // Generate texture and rotate based on direction
    const tempKey = `${key}_temp`;
    graphics.generateTexture(tempKey, size, size);
    graphics.destroy();

    // Create a render texture to apply rotation
    const renderTexture = scene.add.renderTexture(0, 0, size, size);
    const tempSprite = scene.add.sprite(size / 2, size / 2, tempKey);

    // Apply rotation based on direction
    switch (direction) {
      case "up":
        tempSprite.setAngle(0);
        break;
      case "right":
        tempSprite.setAngle(90);
        break;
      case "down":
        tempSprite.setAngle(180);
        break;
      case "left":
        tempSprite.setAngle(270);
        break;
    }

    renderTexture.draw(tempSprite, size / 2, size / 2);
    renderTexture.saveTexture(key);

    // Clean up
    tempSprite.destroy();
    renderTexture.destroy();
    scene.textures.remove(tempKey);
  }

  getDirection(): ArrowDirection {
    return this.direction;
  }

  /**
   * Rotate the arrow 90 degrees clockwise
   * up → right → down → left → up
   */
  rotate(): void {
    switch (this.direction) {
      case "up":
        this.direction = "right";
        break;
      case "right":
        this.direction = "down";
        break;
      case "down":
        this.direction = "left";
        break;
      case "left":
        this.direction = "up";
        break;
    }
  }

  clone(): ArrowTile {
    const copy = new ArrowTile({
      col: this.col,
      row: this.row,
      direction: this.direction,
      id: this.id,
    });
    copy.sprite = this.sprite;
    return copy;
  }

  toJSON(): Record<string, any> {
    return {
      type: "arrow",
      direction: this.direction,
      id: this.id,
      col: this.col,
      row: this.row,
    };
  }
}
